import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";
import { useCompanyFilter } from "@/lib/companyFilter";
import { FileSpreadsheet, Users } from "lucide-react";

const downloadBlob = async (url, filename) => {
  const res = await api.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Headcount report — department / type / status / recent joiners.
 */
export default function HeadcountReport() {
  const { companyFilter, empParams, companyId, filterEmployees } = useCompanyFilter();
  const [charts, setCharts] = useState(null);
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/stats/charts"),
      api.get("/employees", { params: empParams }),
    ]).then(([c, e]) => {
      setCharts(c.data);
      setEmps(filterEmployees(e.data || []));
    }).catch(() => {
      setCharts(null);
      setEmps([]);
    });
  }, [companyId, empParams]);

  const byStatus = useMemo(() => {
    const map = {};
    for (const e of emps) {
      const st = e.employment?.status || "Unknown";
      map[st] = (map[st] || 0) + 1;
    }
    return Object.entries(map)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  }, [emps]);

  const total = emps.length;
  const active = byStatus.find((s) => s.status === "Active")?.count || 0;

  const exportExcel = async () => {
    setBusy(true);
    try {
      await downloadBlob("/exports/headcount.xlsx", `headcount_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      alert("Export failed");
    } finally {
      setBusy(false);
    }
  };

  const depRows = charts?.by_department || [];
  const typeRows = charts?.by_employment_type || [];
  const joinerRows = charts?.joiners_last_6_months || [];

  return (
    <AdminLayout title="Headcount Report" breadcrumbs={["Home", "Reports", "Headcount"]}>
      <div className="space-y-4">
        <FilterBar
          filters={[companyFilter]}
          rightSlot={
            <button
              type="button"
              disabled={busy}
              onClick={exportExcel}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 bg-white disabled:opacity-60"
              data-testid="headcount-export-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
            </button>
          }
        />
        <p className="text-sm text-slate-600">
          Snapshot of workforce by department, employment type, and status — plus joiners over the last 6 months.
          Charts below may still reflect organisation-wide totals when “All companies” is selected.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total employees" value={total} icon />
          <StatCard label="Active" value={active} />
          <StatCard label="Departments" value={depRows.length} />
          <StatCard
            label="Joiners (6 mo)"
            value={joinerRows.reduce((s, j) => s + Number(j.joiners || 0), 0)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TableCard title="By Department" headers={["Department", "Active", "Onboarding", "Inactive", "Total"]}>
            {depRows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No data</td></tr>
            ) : (
              depRows.map((d) => (
                <tr key={d.department}>
                  <td className="px-4 py-2 text-slate-800 font-medium">{d.department}</td>
                  <td className="px-4 py-2 text-slate-600">{d.active}</td>
                  <td className="px-4 py-2 text-slate-600">{d.onboarding}</td>
                  <td className="px-4 py-2 text-slate-600">{d.inactive}</td>
                  <td className="px-4 py-2 text-slate-800 font-semibold">{d.total}</td>
                </tr>
              ))
            )}
          </TableCard>

          <TableCard title="By Employment Type" headers={["Type", "Count"]}>
            {typeRows.length === 0 ? (
              <tr><td colSpan={2} className="px-4 py-6 text-center text-slate-400">No data</td></tr>
            ) : (
              typeRows.map((t) => (
                <tr key={t.type}>
                  <td className="px-4 py-2 text-slate-800 font-medium">{t.type}</td>
                  <td className="px-4 py-2 text-slate-700">{t.count}</td>
                </tr>
              ))
            )}
          </TableCard>

          <TableCard title="By Status" headers={["Status", "Count"]}>
            {byStatus.length === 0 ? (
              <tr><td colSpan={2} className="px-4 py-6 text-center text-slate-400">No data</td></tr>
            ) : (
              byStatus.map((s) => (
                <tr key={s.status}>
                  <td className="px-4 py-2 text-slate-800 font-medium">{s.status}</td>
                  <td className="px-4 py-2 text-slate-700">{s.count}</td>
                </tr>
              ))
            )}
          </TableCard>

          <TableCard title="Joiners — last 6 months" headers={["Month", "Joiners"]}>
            {joinerRows.length === 0 ? (
              <tr><td colSpan={2} className="px-4 py-6 text-center text-slate-400">No data</td></tr>
            ) : (
              joinerRows.map((j) => (
                <tr key={j.label}>
                  <td className="px-4 py-2 text-slate-800 font-medium">{j.label}</td>
                  <td className="px-4 py-2 text-slate-700">{j.joiners}</td>
                </tr>
              ))
            )}
          </TableCard>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-4">
      <div className="text-xs text-slate-500 flex items-center gap-1">
        {icon ? <Users className="h-3.5 w-3.5 text-[#226DB4]" /> : null}
        {label}
      </div>
      <div className="text-2xl font-semibold text-slate-800 mt-1">{value}</div>
    </div>
  );
}

function TableCard({ title, headers, children }) {
  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 text-sm font-semibold text-slate-700">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              {headers.map((h) => (
                <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">{children}</tbody>
        </table>
      </div>
    </div>
  );
}
